import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { DispositionMatrixResponse, TierCode } from '../../services/benchmarkService';
import { NotComputed, PanelError, PanelSkeleton, SectionCard } from './BenchmarkPrimitives';
import { QualifiedBadge, WithheldNotice } from './BenchmarkQualifiers';
import { NO_WITHHELD, PANEL_FAMILIES, WithheldIndex, qualificationsFor, suppressionReasons } from './withheldMetrics';
import { TIERS_BY_URGENCY, count, percent, tierMarkColor, tierMeta } from './BenchmarkVocabulary';
import { dsVar, ink } from '../../theme/tokens';
import { useThemeMode } from '../../theme/ThemeModeProvider';

const WIDTH = 820;
const HEIGHT = 360;
const NODE_WIDTH = 16;
const NODE_GAP = 14;
const LEFT_X = 150;
const RIGHT_X = WIDTH - 150 - NODE_WIDTH;

interface NodeBox {
    tier: string;
    y: number;
    height: number;
    total: number;
}

/** Stacks one column of tier blocks, sized by share of the caseload. */
function layoutColumn(totals: Map<string, number>, grandTotal: number): Map<string, NodeBox> {
    const usable = HEIGHT - NODE_GAP * (TIERS_BY_URGENCY.length - 1);
    const boxes = new Map<string, NodeBox>();
    let y = 0;

    for (const tier of TIERS_BY_URGENCY) {
        const total = totals.get(tier) || 0;
        const height = grandTotal > 0 ? Math.max(2, (total / grandTotal) * usable) : 0;
        boxes.set(tier, { tier, y, height, total });
        y += height + NODE_GAP;
    }
    return boxes;
}

interface TierMigrationChartProps {
    matrix: DispositionMatrixResponse | null;
    /** Concordance with the incumbent's tiers can be withheld, e.g. with no approved tier mapping. */
    withheld?: WithheldIndex;
    loading?: boolean;
    error?: string | null;
    onRetry?: () => void;
}

/**
 * Where alerts move between the current rules' priority and the agent's.
 *
 * The panel was titled "Queue Inversion & Tier Migration (Sankey Flow)" — the
 * name of the chart type, in a heading, on a page for compliance staff. What the
 * reader needs to know is which alerts changed priority and whether the ones
 * that moved up turned out to matter.
 */
const TierMigrationChart: React.FC<TierMigrationChartProps> = ({ matrix, withheld = NO_WITHHELD, loading = false, error, onRetry }) => {
    const [hovered, setHovered] = useState<string | null>(null);
    const { resolved } = useThemeMode();

    const model = useMemo(() => {
        if (!matrix) return null;

        const incumbentTotals = new Map<string, number>();
        for (const link of matrix.tierMigrationFlow) {
            incumbentTotals.set(link.fromIncumbent, (incumbentTotals.get(link.fromIncumbent) || 0) + link.count);
        }
        const agentTotals = new Map<string, number>(matrix.agentTiers.map((t) => [t.tier, t.totalCount]));

        const grandTotal = matrix.totalAlertCount
            || [...agentTotals.values()].reduce((a, b) => a + b, 0);
        if (!grandTotal) return null;

        const left = layoutColumn(incumbentTotals, grandTotal);
        const right = layoutColumn(agentTotals, grandTotal);

        // Running offsets so ribbons stack inside their node instead of overlapping.
        const leftUsed = new Map<string, number>();
        const rightUsed = new Map<string, number>();

        // Lower index means more urgent, so a smaller index on the right is a
        // move up. An unknown tier yields -1 and is never treated as elevated.
        const urgencyOf = (tier: string) => TIERS_BY_URGENCY.indexOf(tier as TierCode);
        const isElevation = (from: string, to: string) => {
            const a = urgencyOf(from);
            const b = urgencyOf(to);
            return a >= 0 && b >= 0 && b < a;
        };

        const ribbons = matrix.tierMigrationFlow
            .filter((link) => left.has(link.fromIncumbent) && right.has(link.toAgent) && link.count > 0)
            // Elevations paint last so they sit on top: they are the claim the
            // panel is making.
            .sort((a, b) => Number(isElevation(a.fromIncumbent, a.toAgent)) - Number(isElevation(b.fromIncumbent, b.toAgent)))
            .map((link) => {
                const from = left.get(link.fromIncumbent) as NodeBox;
                const to = right.get(link.toAgent) as NodeBox;
                const fromShare = from.total > 0 ? (link.count / from.total) * from.height : 0;
                const toShare = to.total > 0 ? (link.count / to.total) * to.height : 0;

                const y0 = from.y + (leftUsed.get(link.fromIncumbent) || 0);
                const y1 = to.y + (rightUsed.get(link.toAgent) || 0);
                leftUsed.set(link.fromIncumbent, (leftUsed.get(link.fromIncumbent) || 0) + fromShare);
                rightUsed.set(link.toAgent, (rightUsed.get(link.toAgent) || 0) + toShare);

                const x0 = LEFT_X + NODE_WIDTH;
                const x1 = RIGHT_X;
                const mid = (x0 + x1) / 2;

                return {
                    id: `${link.fromIncumbent}->${link.toAgent}`,
                    link,
                    elevated: isElevation(link.fromIncumbent, link.toAgent),
                    path: [
                        `M ${x0},${y0}`,
                        `C ${mid},${y0} ${mid},${y1} ${x1},${y1}`,
                        `L ${x1},${y1 + toShare}`,
                        `C ${mid},${y1 + toShare} ${mid},${y0 + fromShare} ${x0},${y0 + fromShare}`,
                        'Z'
                    ].join(' ')
                };
            });

        return { left, right, ribbons, grandTotal };
    }, [matrix]);

    if (error) {
        return (
            <SectionCard title="Where your alerts move" icon={<CompareArrowsIcon fontSize="small" />}>
                <PanelError message={error} onRetry={onRetry} />
            </SectionCard>
        );
    }
    if (loading) return <PanelSkeleton height={460} />;
    if (!matrix) return null;

    const hoveredRibbon = model?.ribbons.find((r) => r.id === hovered);

    return (
        <SectionCard
            title="Where your alerts move"
            subtitle="Each band is a group of alerts, from the priority your current rules gave them to the priority the agent gave them."
            icon={<CompareArrowsIcon fontSize="small" />}
            accent="#A855F7"
            badges={<QualifiedBadge qualifications={qualificationsFor(withheld, PANEL_FAMILIES.concordance)} />}
        >
            {suppressionReasons(withheld, PANEL_FAMILIES.concordance).length > 0 ? (
                <WithheldNotice reasons={suppressionReasons(withheld, PANEL_FAMILIES.concordance)} />
            ) : !model ? (
                <NotComputed detail="This run recorded no scored alerts to compare." />
            ) : (
                <>
                    <Box className="flex justify-between mb-2 px-2">
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: dsVar('fg-muted') }}>
                            Your current rules
                        </Typography>
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: dsVar('fg-muted') }}>
                            With the agent
                        </Typography>
                    </Box>

                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width="100%" height={HEIGHT} role="img"
                            aria-label="Alert movement between current rule priority and agent priority">
                            {model.ribbons.map((ribbon) => {
                                const color = tierMarkColor(ribbon.link.toAgent, resolved);
                                const active = hovered === ribbon.id;
                                return (
                                    <path
                                        key={ribbon.id}
                                        d={ribbon.path}
                                        fill={color}
                                        fillOpacity={active ? 0.55 : ribbon.elevated ? 0.3 : 0.12}
                                        style={{ cursor: 'pointer', transition: 'fill-opacity 150ms' }}
                                        onMouseEnter={() => setHovered(ribbon.id)}
                                        onMouseLeave={() => setHovered(null)}
                                    >
                                        <title>
                                            {`${count(ribbon.link.count)} alerts moved from ${tierMeta(ribbon.link.fromIncumbent).label} to ${tierMeta(ribbon.link.toAgent).label}. ${count(ribbon.link.truePositives)} were confirmed.`}
                                        </title>
                                    </path>
                                );
                            })}

                            {[['left', model.left, LEFT_X], ['right', model.right, RIGHT_X]].map(([side, boxes, x]) => (
                                <g key={side as string}>
                                    {[...(boxes as Map<string, NodeBox>).values()].map((box) => {
                                        const meta = tierMeta(box.tier);
                                        const isLeft = side === 'left';
                                        return (
                                            <g key={box.tier}>
                                                <rect
                                                    x={x as number}
                                                    y={box.y}
                                                    width={NODE_WIDTH}
                                                    height={box.height}
                                                    fill={tierMarkColor(box.tier, resolved)}
                                                    rx={3}
                                                />
                                                <text
                                                    x={isLeft ? (x as number) - 10 : (x as number) + NODE_WIDTH + 10}
                                                    y={box.y + box.height / 2 - 4}
                                                    textAnchor={isLeft ? 'end' : 'start'}
                                                    style={{ fill: dsVar('fg-body') }}
                                                    fontSize={12}
                                                    fontWeight={600}
                                                >
                                                    {meta.label}
                                                </text>
                                                <text
                                                    x={isLeft ? (x as number) - 10 : (x as number) + NODE_WIDTH + 10}
                                                    y={box.y + box.height / 2 + 11}
                                                    textAnchor={isLeft ? 'end' : 'start'}
                                                    style={{ fill: dsVar('fg-muted') }}
                                                    fontSize={11}
                                                >
                                                    {count(box.total)} alerts
                                                </text>
                                            </g>
                                        );
                                    })}
                                </g>
                            ))}
                        </svg>
                    </Box>

                    {/*
                      A hover readout rather than prose. The previous version put
                      "143 rescued risks" in the panel copy as a string literal,
                      so it said 143 whatever the run contained.
                    */}
                    <Box className="mt-2 rounded-xl bg-page/60 border border-line px-4 py-3 min-h-[52px] flex items-center">
                        {hoveredRibbon ? (
                            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), lineHeight: 1.6 }}>
                                <Box component="span" sx={{ fontWeight: 700, color: dsVar('fg') }}>
                                    {count(hoveredRibbon.link.count)} alerts
                                </Box>{' '}
                                your rules placed in {tierMeta(hoveredRibbon.link.fromIncumbent).label}, the agent placed in{' '}
                                {tierMeta(hoveredRibbon.link.toAgent).label}.{' '}
                                <Box component="span" sx={{ color: ink('#10B981'), fontWeight: 700 }}>
                                    {count(hoveredRibbon.link.truePositives)}
                                </Box>{' '}
                                {hoveredRibbon.link.truePositives === 1 ? 'was' : 'were'} confirmed by investigators
                                {hoveredRibbon.link.sars > 0 && `, and ${count(hoveredRibbon.link.sars)} led to a SAR`}.
                            </Typography>
                        ) : (
                            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>
                                Hover a band to see how many alerts moved that way and how many turned out to be genuine.
                            </Typography>
                        )}
                    </Box>

                    <Box className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                        {matrix.agentTiers.map((tier) => {
                            const meta = tierMeta(tier.tier);
                            return (
                                <Box key={tier.tier} className="rounded-xl bg-page/60 border border-line p-3">
                                    <Box className="flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tierMarkColor(tier.tier, resolved) }} />
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: dsVar('fg-soft') }}>
                                            {meta.label}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '1.125rem', fontWeight: 800, color: dsVar('fg'), mt: 0.5 }}>
                                        {percent(tier.hitRate)}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                                        confirmed, of {count(tier.totalCount)} alerts
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
        </SectionCard>
    );
};

export default TierMigrationChart;
